import { useMemo, useState } from "react";

/**
 * El texto de una nota de bitácora, plegado cuando es largo.
 *
 * Una nota puede traer el cuerpo entero de un correo: la del `CASO-2026-00113`
 * mide 4,213 caracteres y se comía la pantalla completa, dejando la bitácora
 * ilegible como línea de tiempo — que es para lo que sirve. Joe, al verla:
 * *«es HUGE bro jajaja… ¿se puede poner un botón de minimizar y que quede solo
 * un tag y las primeras dos líneas?»*.
 *
 * Qué se conserva plegado, y por qué:
 * - **La primera línea siempre**, entera. En las notas de correo es el
 *   encabezado («Correo entrante de X: asunto»), o sea lo que dice de qué se
 *   trata. Recortarla dejaría la nota sin identidad.
 * - **Dos líneas más** del cuerpo, para saber si vale la pena abrirla.
 * - **Cuánto falta**, en el botón: «ver todo (58 líneas)» informa mucho más que
 *   un «ver más» pelado — dice si te vas a topar con un párrafo o con un muro.
 *
 * Las notas cortas no se tocan: no hay botón, no hay estado, no hay nada que
 * aprender. Solo se pliega lo que de verdad estorba.
 */

/** A partir de aquí una nota estorba más de lo que informa. */
const LINEAS_TOPE = 4;
const CHARS_TOPE = 320;

export default function NotaTexto({
  texto,
  className = "",
}: {
  texto: string;
  className?: string;
}) {
  const [abierta, setAbierta] = useState(false);

  const { larga, resumen, lineas } = useMemo(() => {
    const partes = texto.split("\n");
    // Las líneas en blanco no cuentan para decidir: un correo con doble salto
    // entre párrafos parecería largo teniendo tres frases.
    const conTexto = partes.filter((l) => l.trim() !== "");
    const esLarga = conTexto.length > LINEAS_TOPE || texto.length > CHARS_TOPE;
    if (!esLarga) return { larga: false, resumen: texto, lineas: conTexto.length };

    // La primera línea con texto (el encabezado) + dos más.
    const elegidas: string[] = [];
    for (const l of partes) {
      if (l.trim() !== "") elegidas.push(l);
      if (elegidas.length === 3) break;
    }
    return { larga: true, resumen: elegidas.join("\n"), lineas: conTexto.length };
  }, [texto]);

  if (!larga) {
    return <p className={`whitespace-pre-wrap text-sm text-zinc-200 ${className}`}>{texto}</p>;
  }

  return (
    <div className={className}>
      <p className="whitespace-pre-wrap text-sm text-zinc-200">
        {abierta ? texto : resumen}
        {!abierta && <span className="text-zinc-500"> …</span>}
      </p>
      <button
        type="button"
        onClick={() => setAbierta((v) => !v)}
        className="mt-1 rounded text-xs text-accent-400 hover:underline
                   focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-500"
      >
        {abierta ? "▲ ver menos" : `▼ ver todo (${lineas} líneas)`}
      </button>
    </div>
  );
}
